import React from 'react';
import { motion } from 'framer-motion';
interface SkeletonProps {
  className?: string;
  variant?: 'text' | 'circular' | 'rectangular';
  animation?: 'pulse' | 'wave' | 'none';
  width?: string | number;
  height?: string | number;
}
export function Skeleton({
  className = '',
  variant = 'rectangular',
  animation = 'pulse',
  width,
  height
}: SkeletonProps) {
  const baseClasses = 'bg-gray-200 dark:bg-gray-700';
  const variantClasses = {
    text: 'rounded h-4',
    circular: 'rounded-full',
    rectangular: 'rounded-lg'
  };
  const animationVariants = {
    pulse: {
      opacity: [0.5, 1, 0.5],
      transition: {
        duration: 1.5,
        repeat: Infinity,
        ease: 'easeInOut'
      }
    },
    wave: {
      backgroundPosition: ['200% 0', '-200% 0'],
      transition: {
        duration: 1.5,
        repeat: Infinity,
        ease: 'linear'
      }
    },
    none: {}
  };
  const style: React.CSSProperties = {
    width: width || undefined,
    height: height || undefined
  };
  if (animation === 'wave') {
    return <div className={`${baseClasses} ${variantClasses[variant]} ${className} overflow-hidden relative`} style={style} data-id="element-2570">
        <motion.div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent" animate={{
        x: ['-100%', '100%']
      }} transition={{
        duration: 1.5,
        repeat: Infinity,
        ease: 'linear'
      }} data-id="element-2571" />
      </div>;
  }
  return <motion.div className={`${baseClasses} ${variantClasses[variant]} ${className}`} style={style} animate={animationVariants[animation]} data-id="element-2572" />;
}
// Preset skeleton layouts
export function DashboardSkeleton() {
  return <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4 sm:p-6 lg:p-8" data-id="element-2573">
      {/* Header */}
      <div className="mb-8" data-id="element-2574">
        <Skeleton className="h-8 w-48 mb-2" data-id="element-2575" />
        <Skeleton className="h-4 w-64" variant="text" data-id="element-2576" />
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8" data-id="element-2577">
        {[1, 2, 3, 4].map(i => <div key={i} className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700" data-id="element-2578">
            <Skeleton className="h-4 w-24 mb-4" variant="text" data-id="element-2579" />
            <Skeleton className="h-8 w-32 mb-2" data-id="element-2580" />
            <Skeleton className="h-3 w-20" variant="text" data-id="element-2581" />
          </div>)}
      </div>

      {/* Main Content */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700" data-id="element-2582">
        <Skeleton className="h-6 w-48 mb-6" data-id="element-2583" />
        <div className="space-y-4" data-id="element-2584">
          {[1, 2, 3, 4, 5].map(i => <div key={i} className="flex items-center gap-4" data-id="element-2585">
              <Skeleton variant="circular" className="w-12 h-12" data-id="element-2586" />
              <div className="flex-1" data-id="element-2587">
                <Skeleton className="h-4 w-3/4 mb-2" variant="text" data-id="element-2588" />
                <Skeleton className="h-3 w-1/2" variant="text" data-id="element-2589" />
              </div>
              <Skeleton className="h-8 w-24" data-id="element-2590" />
            </div>)}
        </div>
      </div>
    </div>;
}
export function CardSkeleton() {
  return <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700" data-id="element-2591">
      <div className="flex items-start justify-between mb-4" data-id="element-2592">
        <Skeleton variant="circular" className="w-12 h-12" data-id="element-2593" />
        <Skeleton className="h-6 w-6" variant="circular" data-id="element-2594" />
      </div>
      <Skeleton className="h-5 w-3/4 mb-2" variant="text" data-id="element-2595" />
      <Skeleton className="h-4 w-full mb-4" variant="text" data-id="element-2596" />
      <div className="flex gap-2" data-id="element-2597">
        <Skeleton className="h-8 w-20" data-id="element-2598" />
        <Skeleton className="h-8 w-20" data-id="element-2599" />
      </div>
    </div>;
}
export function TableSkeleton({
  rows = 5
}: {
  rows?: number;
}) {
  return <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden" data-id="element-2600">
      <div className="p-6 border-b border-gray-200 dark:border-gray-700" data-id="element-2601">
        <Skeleton className="h-6 w-48" data-id="element-2602" />
      </div>
      <div className="divide-y divide-gray-200 dark:divide-gray-700" data-id="element-2603">
        {Array.from({
        length: rows
      }).map((_, i) => <div key={i} className="p-6 flex items-center gap-4" data-id="element-2604">
            <Skeleton variant="circular" className="w-10 h-10" data-id="element-2605" />
            <div className="flex-1 grid grid-cols-4 gap-4" data-id="element-2606">
              <Skeleton className="h-4" variant="text" data-id="element-2607" />
              <Skeleton className="h-4" variant="text" data-id="element-2608" />
              <Skeleton className="h-4" variant="text" data-id="element-2609" />
              <Skeleton className="h-4 w-20" data-id="element-2610" />
            </div>
          </div>)}
      </div>
    </div>;
}